/**
 * Leagues / leaderboards. PRIVATE by default (explicit opt-in to appear).
 * The core only ranks and assigns tiers; the app decides visibility.
 */

export interface LeaderboardEntry {
  playerId: string;
  xp: number;
  /** Did the player consent to appear on boards? Default: excluded. */
  opdedIn?: boolean;
  displayName?: string;
}

export interface RankedEntry extends LeaderboardEntry {
  rank: number;
}

/**
 * Sorts by XP desc, tiebreak by playerId (stable/deterministic). Only
 * includes those who opted in (privacy by default).
 */
export function rankLeaderboard(entries: readonly LeaderboardEntry[]): RankedEntry[] {
  const visible = entries.filter((e) => e.opdedIn === true);
  const sorted = visible.toSorted((a, b) => {
    if (b.xp !== a.xp) {
      return b.xp - a.xp;
    }
    return a.playerId < b.playerId ? -1 : a.playerId > b.playerId ? 1 : 0;
  });
  return sorted.map((e, i) => ({ ...e, rank: i + 1 }));
}

export type LeagueTier = "bronze" | "silver" | "gold" | "diamond";

const TIERS: readonly LeagueTier[] = ["diamond", "gold", "silver", "bronze"];

/**
 * Assigns a tier by percentile within a league (promotion/relegation). The top
 * 10% = diamond, next 20% = gold, next 30% = silver, the rest bronze.
 */
export function assignTier(rank: number, total: number): LeagueTier {
  if (total <= 0) {
    return "bronze";
  }
  const pct = rank / total; // 0..1, lower = better
  if (pct <= 0.1) {
    return TIERS[0]!;
  }
  if (pct <= 0.3) {
    return TIERS[1]!;
  }
  if (pct <= 0.6) {
    return TIERS[2]!;
  }
  return TIERS[3]!;
}
